import { FormControl, Grid, IconButton, InputLabel, Select, TextField, Table, TableHead, TableBody, TableRow, TableCell
    , Typography, Box, InputAdornment } from '@mui/material';
import { Search as IconSearch } from "@mui/icons-material";
import api from "../services/api";
import * as terapiaService from "../services/terapiaService";
import * as terapiaPlanService from "../services/terapiaPlanService";
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setEntity, setData, setValidationMessage, activeValidations
    , setListEstadoVigencia, setListEstado
    , setListPuntuacionAvance, setListPuntuacionPlanificacion
    , setListPeriodicidades } from "../redux/therapyPlanSlice";
import TerapiaSearch from './TerapiaSearch';
import { FormHelperText as HelperText } from '@mui/material';

const TherapyPlan = (props) => {
    const isReadOnly = props.isReadOnly;
    // El plan del terapeuta se distingue del de supervision: el terapeuta
    // elige entre sus propias terapias y no ve vigencia ni estado, que
    // quedan con sus valores por defecto.
    const modoTerapeuta = props.modoTerapeuta === true;
    const idTerapeuta = props.idTerapeuta == null ? 0 : props.idTerapeuta;
    const params = useParams();
    const entity = useSelector((state) => state.therapyPlan.entity);
    const defaultEntity = useSelector((state) => state.therapyPlan.defaultEntity);
    const listEstadoVigencia = useSelector((state) => state.therapyPlan.listEstadoVigencia);
    const listEstado = useSelector((state) => state.therapyPlan.listEstado);
    const listPuntuacionAvance = useSelector((state) => state.therapyPlan.listPuntuacionAvance);
    const listPuntuacionPlanificacion = useSelector((state) => state.therapyPlan.listPuntuacionPlanificacion);
    const listPeriodicidades = useSelector((state) => state.therapyPlan.listPeriodicidades);
    const validate = useSelector((state) => state.therapyPlan.validationActive);
    const dispatch = useDispatch();
    const fechaHoy = new Date();
    const fechaIngresoMinimo = new Date(2010, 1, 1);
    // Referencia a la terapia del plan. Una terapia puede tener mas de un
    // plan, asi que hay que elegirla con la lupa: no se deduce de otro dato.
    const [openTerapiaSearch, setOpenTerapiaSearch] = useState(false);
    const openCloseTerapiaSearch = () => { setOpenTerapiaSearch(!openTerapiaSearch); };
    const [terapia, setTerapia] = useState(null);
    // Terapias del terapeuta, para el plan que registra el mismo terapeuta.
    const [listTerapiasTerapeuta, setListTerapiasTerapeuta] = useState([]);
    const [planesContenidos, setPlanesContenidos] = useState([]);

    var validation = {
        idTerapia: {
            error: entity.idTerapia == null || entity.idTerapia == 0,
            message: "Falta referenciar la terapia",
        },
        fechaInicio: {
            error: Date.parse(entity.fechaInicio) < fechaIngresoMinimo,
            message: "Debe ser una fecha mayor al " + fechaIngresoMinimo.getFullYear(),
        },
        fechaFin: {
            // El plan es un rango de fechas: la fecha fin no puede quedar antes
            // del inicio.
            error: entity.fechaFin == null || Date.parse(entity.fechaFin) < Date.parse(entity.fechaInicio),
            message: "La fecha fin debe ser mayor o igual a la fecha inicio",
        },
        idEstadoVigencia: {
            // El terapeuta no ve la vigencia: queda con su valor por
            // defecto, asi que tampoco se le valida.
            error: !modoTerapeuta && (entity.idEstadoVigencia == null || entity.idEstadoVigencia == 0),
            message: "Falta ingresar el estado de vigencia",
        },
    }

    // Carga los datos de la terapia para mostrarla como referencia.
    // Tambien guarda su IdTipo para filtrar periodos por tipo de terapia.
    const fetchTerapia = useCallback(async (idTerapia) => {
        if (idTerapia == null || idTerapia == 0) {
            setTerapia(null);
            return;
        }

        await api.get("/Terapia/GetTerapiaView?idTerapia=" + idTerapia)
            .then((res) => {
                if (res.data.data != null) {
                    setTerapia(res.data.data);
                    dispatch(setData({ name: "idTerapia", value: res.data.data.id }))
                    dispatch(setData({ name: "participante", value: res.data.data.participante }))
                    dispatch(setData({ name: "terapeuta", value: res.data.data.terapeuta }))

                    // Plan nuevo: numero correlativo tentativo (ultimo + 1). Se
                    // regenera al guardar para concurrencia.
                    if (!params.id || params.id === "0" || params.id == 0) {
                        api.get("/TerapiaPlan/GetsUltimoNumeroPlan?idTerapia=" + res.data.data.id)
                            .then((r) => {
                                if (r?.data?.data != null) dispatch(setData({ name: "numero", value: r.data.data }));
                            })
                            .catch(() => {});
                    }
                }
            })
            .catch((err) => {
                console.log("Err: ", err);
            });
    }, [dispatch, params.id])

    // La fecha fin sale de la periodicidad y la fecha de inicio, igual que en
    // el mantenimiento de periodos.
    const recalculaFechas = useCallback(async (idPeriodicidad, fechaInicio) => {
        if (!fechaInicio) return;
        const fin = await api.get("/DiaFeriado/GetFechaFinPorPeriodicidad?idPeriodicidad=" + idPeriodicidad
                + "&fechaInicio=" + fechaInicio)
            .catch((err) => { console.log("Err: ", err) });
        if (fin && fin.data && fin.data.data) {
            dispatch(setData({ name: "fechaFin", value: moment(fin.data.data).format('yyyy-MM-DD') }));
        }
    }, [dispatch])

    // Planes activos de la misma terapia contenidos en el rango de fechas del
    // plan actual (excluyendo el propio).
    useEffect(() => {
        const idTerapia = entity.idTerapia;
        const fi = entity.fechaInicio;
        const ff = entity.fechaFin;
        if (!idTerapia || idTerapia <= 0) { setPlanesContenidos([]); return; }
        terapiaPlanService.getsTerapiaPlanResumenViewPorTerapia(idTerapia)
            .then((data) => {
                const contenidos = (data || []).filter((p) => {
                    if (p.idTerapiaPlan == null) return false;
                    if (p.idTerapiaPlan === entity.id) return false;
                    if (p.idEstado != null && p.idEstado !== 2) return false;
                    const fiP = p.fechaInicio ? new Date(p.fechaInicio) : null;
                    const ffP = p.fechaFin ? new Date(p.fechaFin) : null;
                    if (!fiP) return false;
                    const fiActual = new Date(fi);
                    const ffActual = ff ? new Date(ff) : null;
                    if (fiP < fiActual) return false;
                    if (ffP && ffActual && ffP > ffActual) return false;
                    return true;
                });
                setPlanesContenidos(contenidos);
            })
            .catch(() => setPlanesContenidos([]));
    }, [entity.idTerapia, entity.fechaInicio, entity.fechaFin, entity.id]);

    const fetchEntity = useCallback(async () => {
        if (!params.id || params.id === "0" || params.id == 0) {
            dispatch(setEntity(defaultEntity));

            // Plan nuevo: la fecha fin sale de la periodicidad, igual que
            // en el mantenimiento de periodos.
            recalculaFechas(defaultEntity.idPeriodicidad, defaultEntity.fechaInicio);

            // Numero correlativo tentativo (ultimo + 1); se regenera al
            // guardar para evitar colisiones por concurrencia.
            if (params.idTerapia > 0) {
                api.get("/TerapiaPlan/GetsUltimoNumeroPlan?idTerapia=" + params.idTerapia)
                    .then((res) => {
                        if (res?.data?.data != null) dispatch(setData({ name: "numero", value: res.data.data }));
                    })
                    .catch(() => {});
            }

            fetchTerapia(params.idTerapia);
            return;
        }

        await api.get("/TerapiaPlan/GetTerapiaPlanView?idTerapiaPlan=" + params.id)
            .then((response) => {
                if (response.data.data == null || response.data.data.length == 0){
                    dispatch(setEntity(defaultEntity));

                    // Plan nuevo: la fecha fin sale de la periodicidad, igual que
                    // en el mantenimiento de periodos.
                    recalculaFechas(defaultEntity.idPeriodicidad, defaultEntity.fechaInicio);

                    fetchTerapia(params.idTerapia);
                }
                else {
                    dispatch(setEntity(response.data.data));

                    fetchTerapia(response.data.data.idTerapia);
                }
            })
            .catch((err) => {
                console.log("Err: ", err);
            });
    }, [params.id, params.idTerapia, defaultEntity, dispatch, recalculaFechas, fetchTerapia])

    // Elige la terapia del plan con la lupa.
    const setTerapiaSeleccionada = (row) => {
        fetchTerapia(row.idTerapia);
    }

    // Terapias activas del terapeuta, listadas como
    // "primer participante - tipo de terapia".
    const fetchListTerapiasTerapeuta = useCallback(async () => {
        if (!modoTerapeuta || idTerapeuta <= 0) {
            setListTerapiasTerapeuta([]);
            return;
        }

        const data = await terapiaService.getsTerapiaResumenViewByTerapeuta(idTerapeuta)
            .catch((err) => {
                console.log("Err: ", err);
                return [];
            });

        setListTerapiasTerapeuta(data || []);
    }, [modoTerapeuta, idTerapeuta])

    // El terapeuta elige su terapia de la lista: el participante y el
    // terapeuta ya vienen en la fila.
    const setTerapiaDelTerapeuta = (event) => {
        const idTerapia = event.target.value;
        const row = (listTerapiasTerapeuta || [])
            .find((x) => String(x.idTerapia) === String(idTerapia));

        dispatch(setData({ name: "idTerapia", value: idTerapia }))

        if (row != null) {
            dispatch(setData({ name: "participante", value: row.participante == null ? "" : row.participante }))
            dispatch(setData({ name: "terapeuta", value: row.terapeuta == null ? "" : row.terapeuta }))
        }

        if (!params.id || params.id === "0" || params.id == 0) {
            const idNum = parseInt(idTerapia, 10);
            if (idNum > 0) {
                api.get("/TerapiaPlan/GetsUltimoNumeroPlan?idTerapia=" + idNum)
                    .then((r) => {
                        if (r?.data?.data != null) dispatch(setData({ name: "numero", value: r.data.data }));
                    })
                    .catch(() => {});
            }
        }
    }
    const fetchListEstadoVigencia = useCallback(async () => {
        const response = await api.get("/Catalogo/GetCatalogosByIdPadreInLista?idPadre=90")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListEstadoVigencia(response?.data?.data || []));
    }, [])
    const fetchListEstado = useCallback(async () => {
        const response = await api.get("/Catalogo/GetCatalogosByIdPadreInLista?idPadre=1")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListEstado(response?.data?.data || []));
    }, [])
    // Escalas de puntuacion de la cabecera del plan: cada combo pide solo su
    // clase de catalogo 320 ("Clases de puntuacion"), para que Escala de
    // avance no ofrezca escalas de planificacion ni al reves.
    const fetchListPuntuacion = useCallback(async () => {
        // 321 = Sesiones de terapia -> Escala de avance (idPuntuacionAvance).
        const avance = await api.get("/Puntuacion/GetsPuntuacionView", { params: { idClase: 321 } })
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListPuntuacionAvance(avance?.data?.data || []));

        // 322 = Planificacion de terapia -> Puntuacion Planificacion
        // (idPuntuacionPlanificacion).
        const planificacion = await api.get("/Puntuacion/GetsPuntuacionView", { params: { idClase: 322 } })
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListPuntuacionPlanificacion(planificacion?.data?.data || []));
    }, [])
    const fetchListPeriodicidades = useCallback(async () => {
        const response = await api.get("/Catalogo/GetCatalogosByIdPadreInLista?idPadre=300")
            .catch((err) => { console.log("Err: ", err) });
        dispatch(setListPeriodicidades(response?.data?.data || []));
    }, [])
    useEffect(() => {
        fetchEntity();
    }, [fetchEntity]);

    useEffect(() => {
        fetchListEstadoVigencia();
        fetchListEstado();
        fetchListPuntuacion();
        fetchListPeriodicidades();
        dispatch(setValidationMessage(messagesValidation()));
    }, []);

    // La lista de terapias depende del terapeuta, que llega del perfil
    // despues del primer render: se pide aparte, cuando ya se conoce.
    useEffect(() => {
        fetchListTerapiasTerapeuta();
    }, [fetchListTerapiasTerapeuta]);

    useEffect(() => {
        dispatch(setValidationMessage(messagesValidation()));
    }, [entity])

    var setDataEntity = e => {
        dispatch(setData(e.target));
    }
    const messagesValidation = () => {
        var messages = [];

        Object.keys(validation).forEach(function (key) {
            if (validation[key].error) messages.push(validation[key].message)
        })

        return messages;
    }

    return (
        <Grid container>
            {/* {JSON.stringify(validation)}
            {JSON.stringify(validate)} */}
            {/* {JSON.stringify(entity)} */}
            <Grid item xs={12}>
                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                    <Typography variant="h7">
                        Datos Basicos
                    </Typography>
                </Box>
            </Grid>
            {/* El titulo va en su propia fila. Si el contenedor de campos no
                ocupa las 12 columnas, la primera linea queda a la altura del
                titulo. El ancho lo reparte el contenedor de la pagina. */}
            <Grid item xs={12}>
            <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                {/* El terapeuta elige entre sus terapias, listadas como
                    "primer participante - tipo de terapia". En supervision se
                    busca la terapia con la lupa. */}
                {modoTerapeuta ? (
                    <Grid item xs={12} sm={12} md={6}>
                        <FormControl required fullWidth size="small" error={validate.idTerapia && validation.idTerapia.error}>
                            <InputLabel shrink htmlFor="input-terapia">Terapia</InputLabel>
                            <Select
                                native
                                label="Terapia"
                                value={entity.idTerapia == null ? 0 : entity.idTerapia}
                                onChange={setTerapiaDelTerapeuta}
                                inputProps={{
                                    name: 'idTerapia',
                                    id: 'input-terapia',
                                }}
                                disabled={isReadOnly}
                                size="small"
                            >
                                <option key={0} value={0}>{"(Seleccione)"}</option>
                                {listTerapiasTerapeuta.map((row) => (
                                    <option key={row.idTerapia} value={row.idTerapia}>
                                        {(row.participante == null ? "" : row.participante)
                                            + " - "
                                            + (row.tipo == null ? "" : row.tipo)}
                                    </option>
                                ))}
                            </Select>
                            <HelperText>
                                {validate.idTerapia && validation.idTerapia.error ? validation.idTerapia.message : ""}
                            </HelperText>
                        </FormControl>
                    </Grid>
                ) : (
                    <>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl required fullWidth size="small" error={validate.idTerapia && validation.idTerapia.error}>
                                <TextField
                                    name="terapia"
                                    label="Terapia"
                                    fullWidth
                                    size="small"
                                    disabled={isReadOnly}
                                    value={terapia == null || entity.idTerapia == 0 || terapia.tipo == null ? ""
                                        : terapia.tipo}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="buscar terapia"
                                                    size="small"
                                                    disabled={isReadOnly}
                                                    onClick={openCloseTerapiaSearch}>
                                                    <IconSearch fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <HelperText>
                                    {validate.idTerapia && validation.idTerapia.error ? validation.idTerapia.message : ""}
                                </HelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                disabled
                                name="participante"
                                label="Participante"
                                fullWidth
                                value={entity.participante}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                disabled
                                name="terapeuta"
                                label="Terapeuta"
                                fullWidth
                                value={entity.terapeuta}
                                size="small"
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={6} sm={3} md={2} lg={2}>
                    <TextField
                        label="Numero"
                        fullWidth
                        value={entity.numero || ""}
                        InputProps={{ readOnly: true }}
                        disabled
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                    <FormControl required fullWidth size="small">
                        <InputLabel htmlFor="input-periodicidad">Periodicidad</InputLabel>
                        <Select
                            native
                            label="Periodicidad"
                            value={entity.idPeriodicidad}
                            onChange={(event) => {
                                const idPeriodicidad = parseInt(event.target.value);
                                dispatch(setData({ name: "idPeriodicidad", value: idPeriodicidad }));
                                const periodicidad = listPeriodicidades?.find((row) => row.id == idPeriodicidad);
                                if (periodicidad) {
                                    dispatch(setData({ name: "periodicidad", value: periodicidad.descripcion }))
                                }
                                recalculaFechas(idPeriodicidad, moment(entity.fechaInicio).format('yyyy-MM-DD'));
                            }}
                            inputProps={{
                                name: 'idPeriodicidad',
                                id: 'input-periodicidad',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listPeriodicidades.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                    <TextField
                        required
                        name="fechaInicio"
                        label="Fecha inicio"
                        fullWidth
                        type="date"
                        value={moment(entity.fechaInicio).format('yyyy-MM-DD')}
                        onChange={(event) => {
                            dispatch(setData({ name: "fechaInicio", value: event.target.value }));
                            recalculaFechas(entity.idPeriodicidad, event.target.value);
                        }}
                        error={validate.fechaInicio && validation.fechaInicio.error}
                        helperText={validate.fechaInicio && validation.fechaInicio.error ? validation.fechaInicio.message : ""}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                    <TextField
                        required
                        name="fechaFin"
                        label="Fecha fin"
                        fullWidth
                        type="date"
                        value={entity.fechaFin == null ? "" : moment(entity.fechaFin).format('yyyy-MM-DD')}
                        onChange={setDataEntity}
                        error={validate.fechaFin && validate.fechaFin.error}
                        helperText={validate.fechaFin && validation.fechaFin.error ? validation.fechaFin.message : ""}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="enunciadoReporte"
                        label="Enunciado reporte"
                        fullWidth
                        multiline
                        minRows={3}
                        value={entity.enunciadoReporte || ""}
                        onChange={setDataEntity}
                        disabled={isReadOnly}
                        size="small"
                    />
                </Grid>
                {/* El terapeuta no ve la vigencia ni el estado: quedan con sus
                    valores por defecto (Vigente y Activo). */}
                {!modoTerapeuta && (
                    <>
                        <Grid item xs={6} sm={6} md={3} lg={3}>
                            <FormControl required fullWidth size="small" error={validate.idEstadoVigencia && validation.idEstadoVigencia.error}>
                                <InputLabel shrink htmlFor="input-estadoVigencia">Vigencia</InputLabel>
                                <Select
                                    native
                                    label="Vigencia"
                                    value={entity.idEstadoVigencia}
                                    onChange={(event, newValue) => {
                                        var id = event.nativeEvent.target.selectedIndex;

                                        dispatch(setData({name: "idEstadoVigencia", value: event.target.value}))
                                        dispatch(setData({name: "estadoVigencia", value: event.nativeEvent.target[id].text}))
                                    }}
                                    inputProps={{
                                        name: 'idEstadoVigencia',
                                        id: 'input-estadoVigencia',
                                    }}
                                    disabled={isReadOnly}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Seleccione)"}</option>
                                    {listEstadoVigencia.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion}</option>
                                    ))}
                                </Select>
                                <HelperText>
                                    {validate.idEstadoVigencia && validation.idEstadoVigencia.error ? validation.idEstadoVigencia.message : ""}
                                </HelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} lg={3}>
                            {/* <FormControl required fullWidth size="small" error={validate.idEstado && validation.idEstado.error}> */}
                            <FormControl required fullWidth size="small">
                                <InputLabel shrink htmlFor="input-estado">Estado</InputLabel>
                                <Select
                                    //native={!sexos.length == 0}
                                    native
                                    //value={personaNatural.idSexo == null ? 33 : personaNatural.idSexo}
                                    // defaultValue={0}
                                    label="Estado"
                                    value={entity.idEstado}
                                    onChange={setDataEntity}
                                    inputProps={{
                                        name: 'idEstado',
                                        id: 'input-estado',
                                    }}
                                    disabled={isReadOnly}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Seleccione)"}</option>
                                    {listEstado.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion}</option>
                                    ))}
                                </Select>
                                {/* <HelperText>{validate.idEstado && validation.idEstado.error ? validation.idEstado.message : ""}</HelperText> */}
                            </FormControl>
                        </Grid>
                    </>
                )}
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel shrink htmlFor="input-puntuacionPlanificacion">Puntuacion Planificacion</InputLabel>
                        <Select
                            native
                            label="Puntuacion Planificacion"
                            value={entity.idPuntuacionPlanificacion == null ? 0 : entity.idPuntuacionPlanificacion}
                            onChange={(event, newValue) => {
                                var id = event.nativeEvent.target.selectedIndex;

                                dispatch(setData({name: "idPuntuacionPlanificacion", value: event.target.value}))
                                dispatch(setData({name: "puntuacionPlanificacion", value: event.nativeEvent.target[id].text}))
                            }}
                            inputProps={{
                                name: 'idPuntuacionPlanificacion',
                                id: 'input-puntuacionPlanificacion',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listPuntuacionPlanificacion.map((row) => (
                                <option key={row.id} value={row.id}>{row.codigo} - {row.nombre}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel shrink htmlFor="input-puntuacionAvance">Escala de avance</InputLabel>
                        <Select
                            native
                            label="Escala de avance"
                            value={entity.idPuntuacionAvance == null ? 0 : entity.idPuntuacionAvance}
                            onChange={(event, newValue) => {
                                var id = event.nativeEvent.target.selectedIndex;

                                dispatch(setData({name: "idPuntuacionAvance", value: event.target.value}))
                                dispatch(setData({name: "puntuacionAvance", value: event.nativeEvent.target[id].text}))
                            }}
                            inputProps={{
                                name: 'idPuntuacionAvance',
                                id: 'input-puntuacionAvance',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listPuntuacionAvance.map((row) => (
                                <option key={row.id} value={row.id}>{row.codigo} - {row.nombre}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel shrink htmlFor="input-detObjetivo">Detallar objetivo</InputLabel>
                        <Select
                            native
                            label="Detallar objetivo"
                            value={entity.detObjetivo === false ? 0 : 1}
                            onChange={(event) => {
                                var si = parseInt(event.target.value) === 1;
                                dispatch(setData({ name: "detObjetivo", value: si }))
                                // Un criterio siempre cuelga de un objetivo: si se
                                // detalla el criterio, el objetivo va en Si.
                                if (si && entity.detCriterio === false) {
                                    dispatch(setData({ name: "detCriterio", value: true }))
                                }
                            }}
                            inputProps={{
                                name: 'detObjetivo',
                                id: 'input-detObjetivo',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={1} value={1}>{"Si"}</option>
                            <option key={0} value={0}>{"No"}</option>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel shrink htmlFor="input-detCriterio">Detallar criterio</InputLabel>
                        <Select
                            native
                            label="Detallar criterio"
                            value={entity.detCriterio === false ? 0 : 1}
                            onChange={(event) => {
                                var si = parseInt(event.target.value) === 1;
                                dispatch(setData({ name: "detCriterio", value: si }))
                                if (si) dispatch(setData({ name: "detObjetivo", value: true }))
                            }}
                            inputProps={{
                                name: 'detCriterio',
                                id: 'input-detCriterio',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={1} value={1}>{"Si"}</option>
                            <option key={0} value={0}>{"No"}</option>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ fontWeight: "bold", mt: 2, mb: 1 }}>Planes Contenidos</Box>
                {planesContenidos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Sin planes contenidos.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Numero</TableCell>
                                <TableCell>Periodicidad</TableCell>
                                <TableCell>Fecha Inicio</TableCell>
                                <TableCell>Fecha Fin</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {planesContenidos.map((p) => (
                                <TableRow key={p.idTerapiaPlan}>
                                    <TableCell>{p.numero}</TableCell>
                                    <TableCell>{p.periodicidad}</TableCell>
                                    <TableCell>{p.fechaInicio ? new Date(p.fechaInicio).toLocaleDateString("es-PE") : ""}</TableCell>
                                    <TableCell>{p.fechaFin ? new Date(p.fechaFin).toLocaleDateString("es-PE") : ""}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Grid>
            {!modoTerapeuta && (
                <TerapiaSearch
                    open={openTerapiaSearch}
                    openClose={openCloseTerapiaSearch}
                    setSelection={setTerapiaSeleccionada}
                />
            )}
        </Grid>
    );
}
export default TherapyPlan;
